import React, { useState } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useLocation } from 'react-router';
import { ErrorModel, ProfileReadExternal, ProfileUpdateExternal } from '@askporter/client-grieg-lyric';
import { Dialog, useToast } from '@askporter/component-library';
import { useTranslation } from '@askporter/config-provider';
import { useProfile } from '@askporter/profile-provider';
import { API } from '../../utils';
import CustomiseView from './CustomiseView';
import InitialView from './InitialView';
interface CookieManagementProps {
  children: React.ReactNode | React.ReactNode[];
}
interface CookiePreferences {
  rum: boolean;
}
const CookieManagement: React.FC<React.PropsWithChildren<CookieManagementProps>> = ({
  children,
}: CookieManagementProps) => {
  const { t } = useTranslation();
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const { data: profileData, isLoading: profileIsLoading } = useProfile();
  const { pathname } = useLocation();
  const hasAlreadySetCookiePreference: boolean =
    typeof profileData?.consent?.cookieConsent === 'boolean' && !!profileData?.consent?.cookieConsentLastUpdated;
  const [dialogView, setDialogView] = useState<'INITIAL' | 'CUSTOMISE'>('INITIAL');
  const [cookies, setCookies] = useState<CookiePreferences>({ rum: true });
  const toggleCookiePreference = (cookie: keyof CookiePreferences) => {
    setCookies((prev) => ({ ...prev, [cookie]: !cookies?.[cookie] }));
  };
  const profileAPIPath = 'profile';
  const mutateProfile = useMutation<AxiosResponse<ProfileReadExternal>, AxiosError<ErrorModel>, ProfileUpdateExternal>({
    mutationFn: (payload: ProfileUpdateExternal) => API().post({ path: profileAPIPath, payload }),
    onMutate: async (profileUpdate) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [profileAPIPath] });
      // Snapshot the previous value
      const previousProfileData: ProfileReadExternal = queryClient.getQueryData([profileAPIPath]);
      // Optimistically update to the new value
      queryClient.setQueryData([profileAPIPath], (old: ProfileReadExternal) => ({ ...old, ...profileUpdate }));
      // Return a context object with the snapshotted value
      return previousProfileData;
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    // and refetch to ensure the cache reflects the real server state
    onError: (_err, _assetUpdate, context: ProfileReadExternal) => {
      showError(t('ns.common:error:problem'));
      queryClient.setQueryData([profileAPIPath], context);
      queryClient.invalidateQueries({ queryKey: [profileAPIPath] });
    },
  });
  const updateProfile = (cookieConsent: boolean) => {
    const consent = { cookieConsent, cookieConsentLastUpdated: new Date().toISOString() };
    mutateProfile.mutate({ consent });
  };
  const handleAcceptAllOnClick = () => {
    setCookies((prev) => {
      const cookiePreferencesAllAccepted = Object.keys(prev || {}).reduce<CookiePreferences>((prev, curr) => {
        return { ...prev, [curr]: true };
      }, prev);
      updateProfile(cookiePreferencesAllAccepted.rum);
      return cookiePreferencesAllAccepted;
    });
  };
  const handleSaveOnClick = () => {
    updateProfile(cookies.rum);
  };
  if (
    hasAlreadySetCookiePreference ||
    profileIsLoading ||
    (!profileIsLoading && !profileData?.uid) ||
    pathname.includes('/verify-number')
  )
    return <>{children}</>;
  else
    return (
      <>
        {children}
        <Dialog isOpen={true}>
          {dialogView === 'INITIAL' ? (
            <InitialView
              t={t}
              setDialogView={setDialogView}
              handleAcceptAllOnClick={handleAcceptAllOnClick}
              isSmallDevice={isSmallDevice}
            />
          ) : null}
          {dialogView === 'CUSTOMISE' ? (
            <CustomiseView
              t={t}
              cookies={cookies}
              toggleCookiePreference={toggleCookiePreference}
              setDialogView={setDialogView}
              handleSaveOnClick={handleSaveOnClick}
            />
          ) : null}
        </Dialog>
      </>
    );
};
export default CookieManagement;
